import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { setAuth, getAuth } from '../../lib/auth';
import { executeRecaptcha } from '../../lib/recaptcha';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { isGenericEmail, BUSINESS_EMAIL_MESSAGE } from '../../lib/businessEmail';
import { passwordError, PASSWORD_RULES, MAX_FIELD_LEN } from '../../lib/passwordPolicy';
import {
  Loader2, Mail, UploadCloud, FileText, CheckCircle2, AlertCircle,
  Sparkles, ArrowRight, X, RefreshCw, Layers, Download, Eye, Save, Check,
} from 'lucide-react';

const CHIVO = { fontFamily: 'Chivo, sans-serif' };
const BLUE = 'hsl(221,100%,33%)';

const PROCESSING_STEPS = [
  'Reading through the file…',
  'Scanning the document layout…',
  'Reviewing the line items…',
  'Checking for commodities…',
  'Looking for account info…',
  'Verifying associated meters & meter reads…',
  'Extracting the data…',
  'Checking current charges…',
  'Confirming the total amount matches…',
  'Sanity-checking the numbers…',
  'Organising the data…',
  'Nearly there…',
];
const PROCESSING_TAIL = [
  'Cross-checking line items…',
  'Verifying meter reads…',
  'Sanity-checking the numbers…',
  'Confirming totals…',
  'Organising the data…',
  'Nearly there…',
];

function StatusBadge({ status }) {
  const map = {
    completed: { c: 'bg-green-100 text-green-800 border-green-200', t: 'Parsed', I: CheckCircle2 },
    processing: { c: 'bg-blue-100 text-blue-800 border-blue-200', t: 'Processing', I: Loader2 },
    uploaded: { c: 'bg-blue-100 text-blue-800 border-blue-200', t: 'Queued', I: Loader2 },
    failed: { c: 'bg-red-100 text-red-800 border-red-200', t: 'Failed', I: AlertCircle },
  };
  const m = map[status] || map.uploaded;
  const I = m.I;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold border ${m.c}`} data-testid={`doc-status-${status}`}>
      <I className={`h-3 w-3 ${(status === 'processing' || status === 'uploaded') ? 'animate-spin' : ''}`} />{m.t}
    </span>
  );
}

// Internal trial engagement tracking (NOT shown to end users) — best-effort.
const trackTry = (accountId, event, label) => {
  if (!accountId || !event) return;
  try { api.post('/try/track', { account_id: accountId, event, label: label || '' }).catch(() => {}); } catch (_) {}
};

export default function TryExtract() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const uid = params.get('uid');
  const testOtp = params.get('test_otp');
  const src = params.get('src') || 'website';

  const auth = getAuth();
  const [step, setStep] = useState(auth.token ? 'workspace' : (uid ? 'verify' : 'email'));
  const [accountId, setAccountId] = useState(auth.accounts?.[0]?.id || null);
  const arrivedLoggedIn = !!(token && auth.token);

  // ---- EMAIL ----
  const [email, setEmail] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [emailPolicy, setEmailPolicy] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [verifyUserId, setVerifyUserId] = useState(uid || null);
  useEffect(() => {
    api.get('/auth/registration-status')
      .then(({ data }) => setEmailPolicy({ blocked_domains: data?.blocked_domains || [], allowed_domains: data?.allowed_domains || [] }))
      .catch(() => {});
  }, []);
  const submitEmail = async (e) => {
    e.preventDefault();
    if (isGenericEmail(email.trim(), emailPolicy)) {
      setEmailErr(BUSINESS_EMAIL_MESSAGE);
      toast.error(BUSINESS_EMAIL_MESSAGE);
      return;
    }
    setEmailErr('');
    setEmailLoading(true);
    try {
      const { data } = await api.post('/try/email', { email: email.trim(), token, src });
      if (data.already_registered) {
        toast.error(data.message);
        setTimeout(() => navigate('/login'), 1200);
        return;
      }
      setVerifyUserId(data.user_id);
      if (data.test_otp) setOtp(data.test_otp.split(''));
      toast.success(data.message || "We've emailed you a code.");
      setStep('verify');
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Could not send the code. Try again.');
    } finally { setEmailLoading(false); }
  };

  // ---- VERIFY ----
  const [otp, setOtp] = useState(testOtp && testOtp.length === 6 ? testOtp.split('') : ['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const otpRefs = useRef([]);
  const submitOtp = async (code) => {
    const codeStr = (code || otp.join('')).slice(0, 6);
    if (codeStr.length !== 6 || !verifyUserId) return;
    setVerifying(true);
    try {
      const { data } = await api.post('/try/verify', { user_id: verifyUserId, otp_code: codeStr });
      setAuth(data.access_token, data.user, data.accounts);
      setAccountId(data.accounts?.[0]?.id || null);
      toast.success("Email verified — let's parse a bill!");
      setStep('workspace');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code. Try again.');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally { setVerifying(false); }
  };
  const onOtpChange = (i, v) => {
    if (!/^\d*$/.test(v)) return;
    const next = [...otp]; next[i] = v.slice(0, 1); setOtp(next);
    if (v && i < 5) otpRefs.current[i + 1]?.focus();
  };
  const resend = async () => {
    if (!verifyUserId) return;
    try {
      const { data } = await api.post('/try/resend', { user_id: verifyUserId });
      toast.success('New code sent.');
      if (data?.test_otp) setOtp(data.test_otp.split(''));
    } catch (e) { toast.error(e.response?.data?.detail || 'Please wait before resending.'); }
  };

  // ---- WORKSPACE ----
  const [docs, setDocs] = useState([]);
  const [quota, setQuota] = useState({ limit: 2, used: 0, remaining: 2, page_limit: 20, pages_used: 0, pages_remaining: 20 });
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('single');
  const [showCoach, setShowCoach] = useState(() => !localStorage.getItem('try_coach_seen'));
  const fileRef = useRef(null);

  // Cycling "AI at work" status phrase while a bill is processing.
  const [stepIdx, setStepIdx] = useState(0);
  useEffect(() => {
    if (step !== 'workspace') return;
    const iv = setInterval(() => setStepIdx((i) => i + 1), 2600);
    return () => clearInterval(iv);
  }, [step]);
  const procPhrase = stepIdx < PROCESSING_STEPS.length
    ? PROCESSING_STEPS[stepIdx]
    : PROCESSING_TAIL[(stepIdx - PROCESSING_STEPS.length) % PROCESSING_TAIL.length];

  const fetchStatus = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get(`/try/status?account_id=${accountId}`);
      setDocs(data.documents || []);
      setQuota(data.quota || { limit: 2, used: 0, remaining: 2, page_limit: 20, pages_used: 0, pages_remaining: 20 });
    } catch (e) { /* transient */ }
  }, [accountId]);

  useEffect(() => {
    if (step !== 'workspace' || !accountId) return;
    fetchStatus();
    const anyPending = docs.some((d) => d.status === 'processing' || d.status === 'uploaded');
    const iv = setInterval(fetchStatus, anyPending ? 4000 : 9000);
    return () => clearInterval(iv);
  }, [step, accountId, fetchStatus, docs]);

  // Internal engagement tracking: first workspace view + active-time heartbeat.
  useEffect(() => {
    if (step !== 'workspace' || !accountId) return;
    trackTry(accountId, 'view_workspace');
    const beat = () => { if (document.visibilityState === 'visible') trackTry(accountId, 'heartbeat'); };
    const iv = setInterval(beat, 20000);
    return () => clearInterval(iv);
  }, [step, accountId]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (!list.length) { toast.error('Please choose a PDF bill.'); return; }
    if (quota.remaining <= 0) { toast.error('Trial limit reached (2 bills).'); return; }
    if ((quota.pages_remaining ?? 20) <= 0) {
      toast.error(`Trial page limit reached — up to ${quota.page_limit ?? 20} pages total. Register a free account to keep parsing.`);
      return;
    }
    setUploading(true);
    trackTry(accountId, 'upload_click', `${list.length} file(s)`);
    for (const file of list.slice(0, quota.remaining)) {
      try {
        const form = new FormData(); form.append('file', file);
        await api.post(`/accounts/${accountId}/documents/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
        });
        toast.success(`${file.name} uploaded — it's in the queue and parsing now.`);
        trackTry(accountId, 'upload_success', file.name);
      } catch (e) { toast.error(e.response?.data?.detail || `Upload failed for ${file.name}`); }
    }
    setUploading(false); fetchStatus();
  };
  const dismissCoach = () => { setShowCoach(false); localStorage.setItem('try_coach_seen', '1'); };

  const doneSingle = docs.filter((d) => d.status === 'completed' && (d.bill_type || 'single') !== 'consolidated');
  const doneConsolidated = docs.filter((d) => d.status === 'completed' && d.bill_type === 'consolidated');
  const inFlight = docs.filter((d) => d.status !== 'completed' && d.status !== 'failed');
  const failed = docs.filter((d) => d.status === 'failed');
  const hasParsed = docs.some((d) => d.status === 'completed');

  // ---- BILL DETAIL (view / edit / save / export) ----
  const [openDoc, setOpenDoc] = useState(null);
  const [detail, setDetail] = useState(null);
  const [jsonText, setJsonText] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);
  const openBill = async (d) => {
    setOpenDoc(d); setDetail(null); setJsonText('');
    trackTry(accountId, 'view_result', d.id);
    try {
      const { data } = await api.get(`/try/document/${d.id}?account_id=${accountId}`);
      setDetail(data);
      setJsonText(JSON.stringify(data.extracted_data || {}, null, 2));
    } catch (e) { toast.error('Could not load the parsed data.'); }
  };
  const saveBill = async () => {
    let parsed;
    try { parsed = JSON.parse(jsonText); }
    catch (e) { toast.error('Invalid JSON — fix the highlighted syntax and try again.'); return; }
    setSavingDoc(true);
    try {
      await api.put(`/try/document/${openDoc.id}?account_id=${accountId}`, { extracted_data: parsed });
      toast.success('Saved.'); fetchStatus();
      trackTry(accountId, 'edit_result', openDoc?.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save.'); }
    finally { setSavingDoc(false); }
  };
  const downloadJson = (d, text) => {
    const content = text != null ? text : JSON.stringify(detail?.extracted_data || {}, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(d.filename || 'bill').replace(/\.pdf$/i, '')}.json`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success('JSON downloaded.');
    trackTry(accountId, 'download_json', d?.id);
  };

  // ---- REGISTER ----
  const [reg, setReg] = useState({ name: auth.user?.name || '', password: '', company: '' });
  const [registering, setRegistering] = useState(false);
  const doRegister = async (e) => {
    e.preventDefault();
    const pwErr = passwordError(reg.password);
    if (pwErr) {
      toast.error(pwErr);
      return;
    }
    setRegistering(true);
    trackTry(accountId, 'register_submit');
    try {
      const { data } = await api.post('/try/register', {
        name: reg.name.trim(), password: reg.password, company: reg.company.trim(),
      });
      setAuth(data.access_token, data.user, data.accounts);
      toast.success("You're in! Here are your bills in the Intake Hub.");
      navigate('/bill-jobs');
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Could not complete registration.'); }
    finally { setRegistering(false); }
  };

  const Header = () => (
    <div className="flex items-center gap-3 mb-8" data-testid="try-brand">
      <img src="/billportal-logo.png" alt="BillPortal" className="h-9 w-9" />
      <span className="text-xl font-black tracking-tighter" style={CHIVO}>BillPortal</span>
      <span className="ml-2 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold text-white" style={{ background: BLUE }}>Free trial</span>
    </div>
  );
  const Rail = ({ active }) => (
    <div className="flex items-center gap-2 mb-8 text-xs uppercase tracking-widest font-bold text-slate-400">
      <span style={active === 'email' || active === 'verify' ? { color: BLUE } : {}}>1 · Verify</span>
      <span className="h-px w-8 bg-slate-200" />
      <span style={active === 'workspace' ? { color: BLUE } : {}}>2 · Extract</span>
      <span className="h-px w-8 bg-slate-200" />
      <span style={active === 'register' ? { color: BLUE } : {}}>3 · Register</span>
    </div>
  );

  const BillRow = (d) => (
    <div key={d.id} className="flex items-center gap-3 border border-slate-200 p-3" data-testid={`try-doc-${d.id}`}>
      {d.bill_type === 'consolidated' ? <Layers className="h-5 w-5 shrink-0" style={{ color: BLUE }} /> : <FileText className="h-5 w-5 text-slate-400 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm truncate">{d.filename}</div>
        <div className="text-xs text-slate-500">
          {d.bill_type === 'consolidated' ? `Consolidated${d.child_count ? ` · ${d.child_count} accounts` : ''}` : 'Single'}
          {d.vendor_name && d.vendor_name !== 'Unknown' ? ` · ${d.vendor_name}` : ''}
        </div>
      </div>
      <StatusBadge status={d.status} />
      {d.status === 'completed' && (
        <Button size="sm" className="rounded-none" style={{ background: BLUE }} onClick={() => { trackTry(accountId, 'open_bill_page', d.id); navigate(`/try/bill/${d.id}?account_id=${accountId}`); }} data-testid={`try-open-${d.id}`}>
          <Eye className="h-3.5 w-3.5 mr-1" /> View
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="try-extract-page">
        <Header />
        <Rail active={step} />

        {/* EMAIL */}
        {step === 'email' && (
          <div className="max-w-md" data-testid="try-email-step">
            <h1 className="text-3xl font-black tracking-tighter mb-2" style={CHIVO}>Try our bill data extraction</h1>
            <p className="text-slate-500 mb-6">Enter your email — we'll send a 6-digit code. Verify it and parse up to 2 bills free, no account needed.</p>
            <form onSubmit={submitEmail} className="space-y-4" data-testid="try-email-form">
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Work email</Label>
                <Input type="email" required maxLength={MAX_FIELD_LEN} autoComplete="email" className="rounded-none mt-1" value={email}
                  onChange={(e) => { setEmail(e.target.value); if (emailErr) setEmailErr(''); }}
                  onBlur={(e) => setEmailErr(isGenericEmail(e.target.value.trim(), emailPolicy) ? BUSINESS_EMAIL_MESSAGE : '')}
                  data-testid="try-email-input" />
                {emailErr && <p className="text-xs text-red-600 mt-1.5" data-testid="try-email-error">{emailErr}</p>}
              </div>
              <Button type="submit" disabled={emailLoading} className="w-full rounded-none h-11 font-bold uppercase tracking-wide" data-testid="try-email-submit">
                {emailLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending code…</> : <>Send verification code <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          </div>
        )}

        {/* VERIFY */}
        {step === 'verify' && (
          <div data-testid="try-verify-step">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 flex items-center justify-center" style={{ background: 'hsl(221,100%,33%,0.1)' }}>
                <Mail className="h-6 w-6" style={{ color: BLUE }} />
              </div>
              <div>
                <h1 className="text-2xl font-black tracking-tighter" style={CHIVO}>Verify your email</h1>
                <p className="text-sm text-slate-500">We've sent a 6-digit code{email ? ` to ${email}` : ''}</p>
              </div>
            </div>
            <div className="flex gap-2 mb-6">
              {otp.map((d, i) => (
                <Input key={i} ref={(el) => (otpRefs.current[i] = el)} type="text" inputMode="numeric" maxLength={1} value={d}
                  onChange={(e) => onOtpChange(i, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus(); }}
                  className="w-12 h-14 text-center text-2xl font-bold rounded-none" data-testid={`try-otp-${i}`} />
              ))}
            </div>
            <Button onClick={() => submitOtp()} disabled={verifying || otp.some((d) => !d)} className="w-full rounded-none h-11 font-bold uppercase tracking-wide" data-testid="try-verify-btn">
              {verifying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying…</> : 'Verify & continue'}
            </Button>
            <button onClick={resend} className="mt-5 inline-flex items-center gap-2 text-sm hover:underline" style={{ color: BLUE }} data-testid="try-resend-btn">
              <RefreshCw className="h-4 w-4" /> Resend code
            </button>
          </div>
        )}

        {/* WORKSPACE */}
        {step === 'workspace' && (
          <div data-testid="try-workspace-step">
            {arrivedLoggedIn && (
              <div className="mb-6 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="try-loggedin-note">
                You're already signed in{auth.user?.email ? ` as ${auth.user.email}` : ''}, so these uploads go to your <b>current workspace</b> (not a fresh trial). Manage them with full features in <a href="/bill-jobs" className="underline font-semibold">the Intake Hub</a>.
              </div>
            )}
            <h1 className="text-3xl font-black tracking-tighter mb-2" style={CHIVO}>Upload a bill to see the AI extract it</h1>
            <p className="text-slate-500 mb-6">
              {quota.remaining > 0 ? (
                <>Try one <b>single</b> and one <b>consolidated</b> bill. View the data, edit it, and download the JSON — no account required.
                <span className="ml-1 text-slate-400">({quota.remaining} of {quota.limit} left)</span></>
              ) : (
                <>You've used your <b>{quota.limit} free parses</b> — open a bill below to view, edit and download its data.</>
              )}
            </p>

            <div className="mb-6 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="try-page-limit-note">
              Free trial parses up to <b>{quota.page_limit ?? 20} pages total</b> across your uploads
              {' '}(<b>{quota.pages_used ?? 0}</b> used · <b>{quota.pages_remaining ?? (quota.page_limit ?? 20)}</b> left). Register a free account for full-size bills.
            </div>

            {showCoach && quota.remaining > 0 && (
              <div className="relative border p-4 mb-6 flex gap-3" style={{ background: 'hsl(221,100%,33%,0.05)', borderColor: 'hsl(221,100%,33%,0.2)' }} data-testid="try-coach">
                <Sparkles className="h-5 w-5 shrink-0 mt-0.5" style={{ color: BLUE }} />
                <div className="text-sm text-slate-700"><b>Tip:</b> Drop a PDF below. Parsed bills appear in the <b>Single</b> or <b>Consolidated</b> tab — open one to view, edit and export its data.</div>
                <button onClick={dismissCoach} className="absolute top-2 right-2 text-slate-400 hover:text-slate-700" data-testid="try-coach-dismiss"><X className="h-4 w-4" /></button>
              </div>
            )}

            {quota.remaining > 0 && (
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                className="border-2 border-dashed border-slate-300 p-8 text-center cursor-pointer hover:border-blue-600 transition-colors mb-8"
                data-testid="try-dropzone"
              >
                <input ref={fileRef} type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="try-file-input" />
                {uploading ? (
                  <><Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin" style={{ color: BLUE }} /><p className="text-sm text-slate-600">Uploading…</p></>
                ) : (
                  <><UploadCloud className="h-8 w-8 mx-auto mb-3 text-slate-400" /><p className="text-sm font-semibold text-slate-700">Drop a PDF here or click to browse</p><p className="text-xs text-slate-400 mt-1">PDF only · up to 50MB</p></>
                )}
              </div>
            )}

            {/* Live upload tracker — shimmer + cycling status so the wait feels intentional */}
            {(inFlight.length > 0 || failed.length > 0) && (
              <div className="mb-6 border border-slate-200" data-testid="try-status-tracker">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2.5 text-sm" style={{ background: 'linear-gradient(90deg,#f5f3ff 0%,#eef2ff 50%,#ecfeff 100%)' }}>
                  <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: 'radial-gradient(circle,#a78bfa,#67e8f9)' }} />
                    <Sparkles className="relative h-4 w-4" style={{ color: '#7c3aed' }} />
                  </span>
                  <span key={stepIdx} className="bp-phrase" data-testid="try-proc-phrase">
                    {inFlight.length ? procPhrase : 'Wrapping up…'}
                  </span>
                </div>
                <div className="divide-y divide-slate-100">
                  {inFlight.map((d) => (
                    <div key={d.id} className="px-4 py-3" data-testid={`try-track-${d.id}`}>
                      <div className="flex items-center gap-3 text-sm mb-2.5">
                        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="flex-1 truncate">{d.filename}</span>
                        <StatusBadge status={d.status} />
                      </div>
                      <div className="space-y-1.5" aria-hidden="true">
                        <div className="h-2.5 bp-shimmer" style={{ width: '82%' }} />
                        <div className="h-2.5 bp-shimmer" style={{ width: '55%' }} />
                        <div className="h-2.5 bp-shimmer" style={{ width: '90%' }} />
                      </div>
                    </div>
                  ))}
                  {failed.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`try-track-${d.id}`}>
                      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                      <span className="flex-1 truncate">{d.filename}</span>
                      <StatusBadge status={d.status} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Parsed bills — Single / Consolidated tabs (intake-hub style) */}
            <Tabs value={tab} onValueChange={setTab} data-testid="try-tabs">
              <TabsList className="rounded-none">
                <TabsTrigger value="single" className="rounded-none" data-testid="try-tab-single">Single ({doneSingle.length})</TabsTrigger>
                <TabsTrigger value="consolidated" className="rounded-none" data-testid="try-tab-consolidated">Consolidated ({doneConsolidated.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="single" className="mt-4 space-y-3">
                {doneSingle.length ? doneSingle.map(BillRow) : <p className="text-sm text-slate-400 py-6 text-center">{inFlight.length ? 'Your bill is still processing — it will appear here once parsed.' : 'No single bills yet — upload one above.'}</p>}
              </TabsContent>
              <TabsContent value="consolidated" className="mt-4 space-y-3">
                {doneConsolidated.length ? doneConsolidated.map(BillRow) : <p className="text-sm text-slate-400 py-6 text-center">{inFlight.length ? 'Your bill is still processing — it will appear here once parsed.' : 'No consolidated bills yet — upload one above.'}</p>}
              </TabsContent>
            </Tabs>

            {/* Task complete → optional next step */}
            {hasParsed && (
              <div className="mt-8 bg-green-50 border border-green-200 p-6" data-testid="try-success">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-black tracking-tight text-green-900" style={CHIVO}>Nice — your bill is parsed! 🎉</span>
                </div>
                <p className="text-sm text-green-800 mb-4">You've finished the parser test. Register to keep these bills in the <b>Intake Hub</b> and explore the platform (5 bills/mo · $5 AI credits · no card).</p>
                <p className="text-sm text-green-900 font-medium mb-4">Register to experience our enterprise-grade utility bill management solution in full capacity — free to get started.</p>
                <Button className="rounded-none h-11 font-bold uppercase tracking-wide" style={{ background: BLUE }} onClick={() => setStep('register')} data-testid="try-next-register">
                  Next — register & explore <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* REGISTER */}
        {step === 'register' && (
          <div className="max-w-md" data-testid="try-register-step">
            <h1 className="text-3xl font-black tracking-tighter mb-2" style={CHIVO}>Create your free account</h1>
            <p className="text-slate-500 mb-6">Your parsed bills appear in the <b>Intake Hub</b>. Name your workspace + set a password.</p>
            <form onSubmit={doRegister} className="space-y-4" data-testid="try-register-form">
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Full name</Label>
                <Input required maxLength={MAX_FIELD_LEN} className="rounded-none mt-1" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} data-testid="try-reg-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Company / workspace name</Label>
                <Input className="rounded-none mt-1" value={reg.company} onChange={(e) => setReg({ ...reg, company: e.target.value })} data-testid="try-reg-company" placeholder="Names your workspace" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Password</Label>
                <Input required type="password" minLength={8} maxLength={MAX_FIELD_LEN} autoComplete="new-password" className="rounded-none mt-1" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} data-testid="try-reg-password" />
                <ul className="mt-2 grid grid-cols-1 gap-1" data-testid="try-reg-password-rules">
                  {PASSWORD_RULES.map((rule) => {
                    const ok = rule.test(reg.password || '');
                    return (
                      <li
                        key={rule.key}
                        className={`flex items-center gap-1.5 text-[11px] ${ok ? 'text-green-600' : 'text-slate-500'}`}
                        data-testid={`try-reg-pw-rule-${rule.key}`}
                      >
                        {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <Button type="submit" disabled={registering} className="w-full rounded-none h-11 font-bold uppercase tracking-wide" data-testid="try-reg-submit">
                {registering ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Finishing…</> : <>Finish & open my workspace <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          </div>
        )}
      </div>

      {/* View / edit bill dialog */}
      <Dialog open={!!openDoc} onOpenChange={(o) => { if (!o) setOpenDoc(null); }}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="try-bill-dialog">
          <DialogHeader>
            <DialogTitle style={CHIVO} className="tracking-tight truncate pr-6">{openDoc?.filename}</DialogTitle>
          </DialogHeader>
          {!detail ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin" style={{ color: BLUE }} /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-x-6 text-xs">
                {[
                  ['Type', openDoc?.bill_type === 'consolidated' ? `Consolidated (${openDoc?.child_count || 0})` : 'Single'],
                  ['Vendor', detail.extracted_data?.vendor_name],
                  ['Total due', detail.extracted_data?.total_amount_due ?? detail.extracted_data?.total_due ?? detail.extracted_data?.new_charges],
                  ['Accuracy', detail.document?.validation_accuracy != null ? `${detail.document.validation_accuracy}%` : null],
                ].filter(([, v]) => v != null && v !== '').map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-200 py-1"><span className="text-slate-500">{k}</span><span className="font-medium text-right ml-2 truncate">{String(v)}</span></div>
                ))}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Parsed data (editable JSON)</Label>
                <Textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)} spellCheck={false}
                  className="rounded-none font-mono text-[11px] h-72 leading-relaxed" data-testid="try-json-editor" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveBill} disabled={savingDoc} className="rounded-none" style={{ background: BLUE }} data-testid="try-save-btn">
                  {savingDoc ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save changes
                </Button>
                <Button variant="outline" className="rounded-none" onClick={() => downloadJson(openDoc, jsonText)} data-testid="try-download-btn">
                  <Download className="h-4 w-4 mr-1" /> Download JSON
                </Button>
                <Button variant="ghost" className="rounded-none ml-auto" onClick={() => setOpenDoc(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
